import { useNavigate, useParams } from "react-router-dom";
import { Compass, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDocumentTitle } from "@/hooks/use-document-title";

/**
 * Shown for an address that does not exist, or no longer does (Costs,
 * Artifacts, Hiring and the other retired pages), instead of React Router's
 * raw "Unexpected Application Error! 404 Not Found" screen.
 *
 * A full-screen page on the app background, covering the sidebar and top bar
 * (Kenneth preferred a clean blackout to showing it inside the app frame).
 * Deliberately short: a heading and one way out, no explanation paragraph.
 * It does not say *why* the page is unavailable, because it cannot tell a
 * mistyped link from a removed page or one the person may not have access to.
 */
export function PageUnavailable() {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId?: string }>();
  useDocumentTitle("Page unavailable");

  // Inside a company the home screen is its dashboard; elsewhere, the root.
  const home = tenantId ? `/tenants/${tenantId}/dashboard` : "/";

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10">
          <Compass size={26} className="text-accent" />
        </div>
        <h1 className="text-xl font-semibold text-text-primary">This page isn&rsquo;t available</h1>
        <div className="mt-6 flex items-center justify-center">
          <Button size="sm" onClick={() => navigate(home, { replace: true })}>
            <Home size={14} className="mr-1.5" />
            Go to home
          </Button>
        </div>
      </div>
    </div>
  );
}
